import { useState } from "react";
import { GraduationCap, Plus, Trash2 } from "lucide-react";
import { PageShell } from "./page-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, ErrorState } from "@/components/common/empty-state";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCourses, useDeleteCourse, useSaveCourse } from "@/lib/queries";
import { useToast } from "@/components/ui/toast";
import type { Course } from "@/lib/types";

export function CoursesPage() {
  const { data, isLoading, isError, refetch } = useCourses();
  const [editing, setEditing] = useState<Course | "new" | null>(null);

  return (
    <PageShell
      title="Courses"
      description="What the bot will recommend. Keep names, durations, and prices accurate."
      actions={
        <Button variant="primary" size="md" onClick={() => setEditing("new")}>
          <Plus className="h-4 w-4" />
          New course
        </Button>
      }
    >
      {isLoading ? (
        <CardSkeletonGrid />
      ) : isError ? (
        <ErrorState
          title="Couldn't load courses"
          description="The API didn't respond."
          onRetry={() => void refetch()}
        />
      ) : !Array.isArray(data) || data.length === 0 ? (
        <EmptyState
          icon={GraduationCap}
          title="No courses yet"
          description="Add your first course so the bot has something to recommend."
          action={
            <Button variant="primary" onClick={() => setEditing("new")}>
              <Plus className="h-4 w-4" />
              New course
            </Button>
          }
        />
      ) : (
        <ul className="grid gap-3 lg:grid-cols-2">
          {data.map((course) => (
            <CourseRow key={course.id} course={course} onEdit={() => setEditing(course)} />
          ))}
        </ul>
      )}

      {editing !== null && (
        <CourseDialog
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </PageShell>
  );
}

function CourseRow({ course, onEdit }: { course: Course; onEdit: () => void }) {
  return (
    <li className="rounded-lg border border-line bg-surface p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-md font-semibold text-ink">{course.name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
            <Badge tone="outline">{course.program}</Badge>
            {course.learning_mode && <Badge tone="neutral">{course.learning_mode}</Badge>}
            {course.duration && <span className="text-ink-muted">{course.duration}</span>}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </div>
      {course.subjects && course.subjects.length > 0 && (
        <p className="mt-3 text-sm text-ink-muted">Subjects: {course.subjects.join(", ")}</p>
      )}
      {course.price !== null && course.price !== undefined && (
        <p className="mt-1 font-mono text-sm text-ink">
          {course.price.toLocaleString()} so'm
        </p>
      )}
      {course.description && (
        <p className="mt-3 line-clamp-3 text-sm text-ink-muted">{course.description}</p>
      )}
    </li>
  );
}

function CardSkeletonGrid() {
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-lg border border-line bg-surface p-5">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="mt-2 h-3 w-24" />
          <Skeleton className="mt-4 h-3 w-full" />
          <Skeleton className="mt-1 h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

function CourseDialog({ initial, onClose }: { initial: Course | null; onClose: () => void }) {
  const saveCourse = useSaveCourse();
  const deleteCourse = useDeleteCourse();
  const toast = useToast();
  const [form, setForm] = useState<Partial<Course>>(
    initial
      ? { ...initial, subjects: initial.subjects ?? [] }
      : {
          name: "",
          program: "SAT",
          duration: "",
          learning_mode: "",
          subjects: [],
          price: 0,
          image_url: "",
          description: "",
        }
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        subjects: typeof form.subjects === "string"
          ? (form.subjects as unknown as string).split(",").map((s) => s.trim()).filter(Boolean)
          : (form.subjects ?? []),
        price: form.price ? Number(form.price) : null,
      };
      await saveCourse.mutateAsync({ id: initial?.id, body });
      toast.success(initial ? "Course updated" : "Course created");
      onClose();
    } catch (err) {
      toast.error("Save failed", err instanceof Error ? err.message : undefined);
    }
  };

  const onDelete = async () => {
    if (!initial) return;
    if (!confirm("Delete this course? This cannot be undone.")) return;
    try {
      await deleteCourse.mutateAsync(initial.id);
      toast.success("Course deleted");
      onClose();
    } catch (err) {
      toast.error("Delete failed", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit course" : "New course"}</DialogTitle>
          <DialogDescription>What the bot recommends when this course is the right fit.</DialogDescription>
        </DialogHeader>

        <form id="course-form" onSubmit={onSubmit} className="space-y-5">
          <section className="space-y-3">
            <FieldRow label="Name" id="name">
              <Input
                id="name"
                value={form.name ?? ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </FieldRow>
            <div className="grid gap-3 sm:grid-cols-2">
              <FieldRow label="Program" id="program">
                <Input
                  id="program"
                  value={form.program ?? ""}
                  onChange={(e) => setForm({ ...form, program: e.target.value })}
                  required
                />
              </FieldRow>
              <FieldRow label="Mode" id="mode" hint="online / offline / hybrid">
                <Input
                  id="mode"
                  value={form.learning_mode ?? ""}
                  onChange={(e) => setForm({ ...form, learning_mode: e.target.value })}
                />
              </FieldRow>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <FieldRow label="Duration" id="duration" hint="e.g. 3 sessions / week">
                <Input
                  id="duration"
                  value={form.duration ?? ""}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                />
              </FieldRow>
              <FieldRow label="Price (so'm)" id="price">
                <Input
                  id="price"
                  type="number"
                  min="0"
                  value={(form.price as number | string | undefined) ?? ""}
                  onChange={(e) => setForm({ ...form, price: e.target.value as unknown as number })}
                />
              </FieldRow>
            </div>
            <FieldRow label="Subjects" id="subjects" hint="Comma-separated">
              <Input
                id="subjects"
                value={Array.isArray(form.subjects) ? form.subjects.join(", ") : (form.subjects ?? "")}
                onChange={(e) => setForm({ ...form, subjects: e.target.value as unknown as string[] })}
                placeholder="Math, English"
              />
            </FieldRow>
          </section>

          <section className="space-y-3">
            <FieldRow label="Description" id="description">
              <Textarea
                id="description"
                rows={6}
                value={form.description ?? ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </FieldRow>
            <FieldRow label="Image URL" id="image_url">
              <Input
                id="image_url"
                value={form.image_url ?? ""}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              />
            </FieldRow>
          </section>
        </form>

        <DialogFooter>
          {initial && (
            <Button
              type="button"
              variant="destructive"
              onClick={onDelete}
              disabled={deleteCourse.isPending}
              className="mr-auto"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="course-form"
            variant="primary"
            disabled={saveCourse.isPending}
          >
            Save course
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FieldRow({
  label,
  id,
  hint,
  children,
}: {
  label: string;
  id: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-ink-faint">{hint}</p>}
    </div>
  );
}
